import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { BookOpen, ArrowRight, Award, Loader2 } from "lucide-react";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return "—";
  }
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [modules, setModules] = useState(null);

  useEffect(() => {
    api.get("/modules").then(({ data }) => setModules(data)).catch(() => setModules([]));
  }, []);

  if (modules === null)
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );

  const passed = modules.filter((m) => m.status === "Passed").length;
  const pct = modules.length ? Math.round((passed / modules.length) * 100) : 0;

  return (
    <div className="space-y-6">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Your training</p>
        <h1 className="mt-1 font-heading text-3xl font-extrabold tracking-tight">Welcome back</h1>
      </div>

      <div className="rounded-2xl border border-border bg-card p-5 shadow-[0_8px_30px_rgba(37,75,54,0.06)]">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-muted-foreground">Overall completion</p>
            <p className="font-heading text-2xl font-extrabold text-primary" data-testid="overall-completion">
              {pct}%
            </p>
          </div>
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <Award className="h-6 w-6" />
          </span>
        </div>
        <Progress value={pct} className="mt-4 h-2.5 bg-secondary" />
        <p className="mt-2 text-xs text-muted-foreground">
          {passed} of {modules.length} modules passed
        </p>
      </div>

      <div className="grid gap-4">
        {modules.map((m, i) => (
          <motion.button
            key={m.id}
            data-testid={`module-card-${m.id}`}
            onClick={() => navigate(`/module/${m.id}`)}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: i * 0.06, ease: "easeOut" }}
            className="group flex w-full items-start gap-4 rounded-2xl border border-border bg-card p-5 text-left shadow-[0_8px_30px_rgba(37,75,54,0.06)] transition-transform duration-200 hover:-translate-y-0.5 active:scale-[0.99]"
          >
            <span className="mt-0.5 flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-secondary text-primary">
              <BookOpen className="h-5 w-5" />
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="font-heading text-lg font-bold tracking-tight">{m.title}</h3>
                <StatusBadge status={m.status} testid={`module-status-${m.id}`} />
              </div>
              <p className="mt-1 text-sm text-muted-foreground">{m.description}</p>
              <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-xs text-muted-foreground">
                {m.final_score != null && (
                  <span>
                    Score: <span className="font-semibold text-foreground">{m.final_score}%</span>
                  </span>
                )}
                {m.status === "Passed" && <span>Completed: {fmtDate(m.completed_at)}</span>}
                {m.attempts > 0 && <span>Attempts: {m.attempts}</span>}
              </div>
            </div>
            <ArrowRight className="mt-1 h-5 w-5 shrink-0 text-muted-foreground transition-transform duration-200 group-hover:translate-x-1" />
          </motion.button>
        ))}
      </div>
    </div>
  );
}
